import { useState } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';

// ============================================================
// REZERVNA KOPIJA — svi podaci korisnika u jedan Excel fajl
// (svaka tabela na svom listu). Čita samo ono što korisnik sme da vidi.
// ============================================================

const TABLES = [
  { table: 'agencies', sheet: 'Nalozi' },
  { table: 'agency_profile', sheet: 'Podaci firmi' },
  { table: 'customers', sheet: 'Klijenti' },
  { table: 'invoices', sheet: 'Fakture' },
  { table: 'tax_payments', sheet: 'Uplate poreza' },
  { table: 'notification_settings', sheet: 'Obavestenja' },
];
const PAGE = 1000;
const MAX_CELL = 32000; // Excel prima najviše ~32 767 znakova u ćeliji

async function fetchAll(table) {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase.from(table).select('*').range(from, from + PAGE - 1);
    if (error) {
      if (/does not exist|schema cache/i.test(error.message)) return rows; // tabela još ne postoji
      throw new Error(`${table}: ${error.message}`);
    }
    rows.push(...(data || []));
    if (!data || data.length < PAGE) return rows;
  }
}

const flatten = (row) => {
  const out = {};
  Object.entries(row).forEach(([k, v]) => {
    if (k === 'logo_data') { out[k] = v ? '(logo sačuvan u aplikaciji)' : ''; return; }
    let val = v;
    if (v !== null && typeof v === 'object') val = JSON.stringify(v);
    if (typeof val === 'string' && val.length > MAX_CELL) val = `${val.slice(0, MAX_CELL)}…`;
    out[k] = val;
  });
  return out;
};

export default function BackupCard() {
  const { tr } = useI18n();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const download = async () => {
    setBusy(true); setMsg(''); setError('');
    try {
      const wb = XLSX.utils.book_new();
      const counts = [];
      for (const { table, sheet } of TABLES) {
        const rows = (await fetchAll(table)).map(flatten);
        const ws = rows.length ? XLSX.utils.json_to_sheet(rows) : XLSX.utils.aoa_to_sheet([['(prazno)']]);
        XLSX.utils.book_append_sheet(wb, ws, sheet);
        counts.push(`${sheet}: ${rows.length}`);
      }
      const d = new Date();
      const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      XLSX.writeFile(wb, `FinArt-rezervna-kopija-${stamp}.xlsx`);
      setMsg(`${tr('Preuzeto', 'Downloaded', 'Скачано')} — ${counts.join(' · ')}`);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>{tr('Rezervna kopija podataka', 'Data backup', 'Резервная копия данных')}</div>
      <p style={styles.text}>
        {tr(
          'Preuzmi sve svoje podatke (nalozi, podaci firmi, klijenti, fakture sa stavkama, uplate poreza) u jedan Excel fajl. Preporuka: jednom nedeljno, i čuvaj fajl na Google Drive-u ili drugom sigurnom mestu.',
          'Download all your data (accounts, company details, clients, invoices with items, tax payments) as one Excel file. Tip: do it weekly and keep the file somewhere safe, e.g. Google Drive.',
          'Скачайте все свои данные (аккаунты, данные фирм, клиенты, счета с позициями, уплаты налогов) в один файл Excel. Совет: раз в неделю и храните файл в надёжном месте, например на Google Drive.',
        )}
      </p>
      {msg && <div style={styles.ok}>{msg}</div>}
      {error && <div style={styles.err}>{error}</div>}
      <button onClick={download} disabled={busy} style={{ ...styles.btn, opacity: busy ? 0.6 : 1 }}>
        <Icon name="download" size={16} />
        {busy ? tr('Pripremam fajl...', 'Preparing file...', 'Готовлю файл...') : tr('Preuzmi rezervnu kopiju (Excel)', 'Download backup (Excel)', 'Скачать резервную копию (Excel)')}
      </button>
    </div>
  );
}

const styles = {
  card: { background: 'var(--surface)', borderRadius: 10, padding: 18, marginBottom: 14, border: '1px solid var(--line)' },
  title: { fontSize: 14, fontWeight: 700, color: 'var(--teal-800)', marginBottom: 8 },
  text: { fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.55, margin: '0 0 12px' },
  btn: { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 16px', fontWeight: 700, fontSize: 14 },
  ok: { background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 10, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 },
  err: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 },
};
